import { backendApi } from "@/services/backend/api";
import {
  Contract,
  Lifetime,
  State,
} from "@/services/backend/vbs/contracts/types/contract";
import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";

const contractApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsContractCreate: build.mutation<Contract, CreateRequest>({
      query: (request) => ({
        url: `/vbs/contract`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "VbsContract", id: "LIST" }],
    }),
    vbsContractCreateForOffer: build.mutation<Contract, CreateForOfferRequest>({
      query: (request) => ({
        url: `/vbs/contract/for-offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "VbsContract", id: "LIST" }, "VbsOffer"],
    }),
    vbsContractShow: build.query<Contract, ShowRequest>({
      query: (request) => ({
        url: "/vbs/contract",
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsContract", id: result.id }] : [],
    }),
    vbsContractList: build.query<Lst<Contract>, Partial<ListRequest>>({
      query: (request) => ({
        url: "/vbs/contract/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((contract) => ({
                type: "VbsContract" as const,
                id: contract.id,
              })),
              { type: "VbsContract", id: "LIST" },
            ]
          : [{ type: "VbsContract", id: "LIST" }],
    }),
    vbsContractCopyFromOffer: build.mutation<Contract, CopyFromOfferRequest>({
      query: (request) => ({
        url: `/vbs/contract/copy-from-offer`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
        "VbsPricing",
        "VbsPricingByArea",
        "VbsCalculation",
        "VbsTreatment",
      ],
    }),
    vbsContractAddTreatmentCycle: build.mutation<
      Contract,
      AddTreatmentCycleRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/treatment-cycle`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractRemoveTreatmentCycle: build.mutation<
      Contract,
      RemoveTreatmentCycleRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/treatment-cycle`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractUpdateLifetime: build.mutation<Contract, UpdateLifetimeRequest>({
      query: (request) => ({
        url: `/vbs/contract/lifetime`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractRegisterScan: build.mutation<
      Contract,
      RegisterSignedContractRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/register-signed-contract`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractTerminate: build.mutation<Contract, TerminateRequest>({
      query: (request) => ({
        url: `/vbs/contract/terminate`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractUpdateCustomerAddress: build.mutation<
      Contract,
      UpdateCustomerAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/customer-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractUpdateServiceAddress: build.mutation<
      Contract,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractUpdatePaymentTerms: build.mutation<
      Contract,
      UpdatePaymentTermsRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/payment-terms`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractUpdateSpecialAgreements: build.mutation<
      Contract,
      UpdateSpecialAgreementsRequest
    >({
      query: (request) => ({
        url: `/vbs/contract/special-agreements`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractPreviewPDF: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/vbs/contract`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractDownloadPDF: build.mutation<
      { filename: string; objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/vbs/contract`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsContract", id: request.id },
      ],
    }),
    vbsContractSetImmutable: build.mutation<Contract, SetImmutableRequest>({
      query: (request) => ({
        url: `/vbs/contract/immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsContract", id: request.id },
        "VbsContract",
        "NumbersDocumentNumber",
      ],
    }),
  }),
});

export const {
  useVbsContractCreateMutation,
  useVbsContractCreateForOfferMutation,
  useVbsContractShowQuery,
  useVbsContractListQuery,
  useVbsContractCopyFromOfferMutation,
  useVbsContractAddTreatmentCycleMutation,
  useVbsContractRemoveTreatmentCycleMutation,
  useVbsContractUpdateLifetimeMutation,
  useVbsContractRegisterScanMutation,
  useVbsContractTerminateMutation,
  useVbsContractUpdateCustomerAddressMutation,
  useVbsContractUpdateServiceAddressMutation,
  useVbsContractUpdatePaymentTermsMutation,
  useVbsContractUpdateSpecialAgreementsMutation,
  useVbsContractPreviewPDFQuery,
  useVbsContractDownloadPDFMutation,
  useVbsContractSetImmutableMutation,
} = contractApi;

export interface CreateRequest {
  id: string;
  processId?: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateForOfferRequest {
  id: string;
  processId?: string;
  offerId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  state?: Filter<State>;
  lifetimeStartDate?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface CopyFromOfferRequest {
  id: string;
  offerId: string;
}

export interface AddTreatmentCycleRequest {
  id: string;
  cycle: number;
}

export interface RemoveTreatmentCycleRequest {
  id: string;
  cycle: number;
}

export interface UpdateLifetimeRequest {
  id: string;
  lifetime: Lifetime;
}

export interface RegisterSignedContractRequest {
  id: string;
  file: File;
  dataFormat: string;
  scannedBy: string;
}

export interface TerminateRequest {
  id: string;
  terminationReason: string;
}

export interface UpdateCustomerAddressRequest {
  id: string;
  customerAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateTreatmentPriceRelativeDiscountRequest {
  id: string;
  active: boolean;
  percentage: number;
}

export interface UpdateTreatmentPriceAbsoluteDiscountRequest {
  id: string;
  active: boolean;
  amount: number;
}

export interface UpdatePricesForExtrasRequest {
  id: string;
  hourlyRate: number;
  ratePerAccessPanel: number;
}

export interface UpdatePaymentTermsRequest {
  id: string;
  terms: string;
}

export interface UpdateSpecialAgreementsRequest {
  id: string;
  agreements: string;
}

export interface SetImmutableRequest {
  id: string;
  reason: string;
  userId: string;
}

export interface PreviewPDFRequest {
  id: string;
  document: ContractDocument;
}

export enum ContractDocument {
  ALL = "all",
  CONTRACT = "contract",
  TREATMENT = "treatment",
  TREATMENT_BY_AREA = "treatment-by-area",
}
